import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { User } from "oidc-client-ts";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getGoogleUserManager,
  getAzureUserManager,
  OidcProvider,
} from "../oidcUserManagers";
import { googleAuthCallback, adAuthCallback } from "~/routes";
import {
  AuthenticationManagerContext,
  AuthenticationManagerContextData,
  AuthenticationManagerDisplay,
} from "./AuthenticationManagerDisplay";
import { loginChannelName, logoutChannelName } from "./common";
import { createBroadcastChannel } from "~/broadcast-channel/createBroadcastChannel";
import "./Auth.scss";
import { UserActiveProvider } from "../useIsActive";
import { Authorizations, GetAuthorizationsAsync } from "../types";
import { getExpiration, isExpired as isTokenExpired } from "../jwtUtils";
import { reportError } from "~/error-handler/reportError";

type AuthenticationManagerProps = {
  getAuthorizations: GetAuthorizationsAsync;
};

export const AuthenticationManager: React.FC<AuthenticationManagerProps> = ({
  getAuthorizations,
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [provider, setProvider] = useState<OidcProvider | null>(null);
  const [authorizations, setAuthorizations] = useState<Authorizations | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const callbackProcessedRef = useRef(false);

  const doLogout = useCallback(
    async (preventPublishToOtherWindows?: boolean) => {
      setUser(null);
      setProvider(null);
      setAuthorizations(null);

      try {
        await getGoogleUserManager().removeUser();
      } catch {
        // ignore
      }
      try {
        await getAzureUserManager().removeUser();
      } catch {
        // ignore
      }

      if (!preventPublishToOtherWindows) {
        const channel = createBroadcastChannel(logoutChannelName);
        const ret = channel.postMessage({ tag: "Logout" }) as any;
        if (ret?.then) {
          ret.then(() => channel.close());
        } else {
          channel.close();
        }
      }
    },
    []
  );

  const handleUserLoaded = useCallback(
    async (loadedUser: User, loadedProvider: OidcProvider) => {
      const idToken = loadedUser.id_token;
      if (!idToken) {
        await doLogout();
        return;
      }

      setUser(loadedUser);
      setProvider(loadedProvider);
      setError(false);

      try {
        const auths = await getAuthorizations(idToken);
        if (auths === null) {
          await doLogout();
          return;
        }
        setAuthorizations(auths);

        const channel = createBroadcastChannel(loginChannelName);
        const ret = channel.postMessage({
          tag: "AuthorizationsUpdated",
          token: idToken,
          provider: loadedProvider,
          authorizations: auths,
        }) as any;
        if (ret?.then) {
          ret.then(() => channel.close());
        } else {
          channel.close();
        }
      } catch (err) {
        reportError(err);
        await doLogout();
      }
    },
    [getAuthorizations, doLogout]
  );

  const handleUserUnloaded = useCallback(() => {
    setUser(null);
    setProvider(null);
    setAuthorizations(null);
  }, []);

  useEffect(() => {
    const googleManager = getGoogleUserManager();
    const azureManager = getAzureUserManager();

    const onGoogleLoaded = (loaded: User) => handleUserLoaded(loaded, "google");
    const onAzureLoaded = (loaded: User) => handleUserLoaded(loaded, "azure");

    googleManager.events.addUserLoaded(onGoogleLoaded);
    googleManager.events.addUserUnloaded(handleUserUnloaded);
    azureManager.events.addUserLoaded(onAzureLoaded);
    azureManager.events.addUserUnloaded(handleUserUnloaded);

    async function checkExistingSession() {
      try {
        const isGoogleCallback = location.pathname === googleAuthCallback.path;
        const isAzureCallback = location.pathname === adAuthCallback.path;

        if (
          (isGoogleCallback || isAzureCallback) &&
          !callbackProcessedRef.current
        ) {
          callbackProcessedRef.current = true;
          const manager = isGoogleCallback ? googleManager : azureManager;

          if (window.self !== window.parent) {
            try {
              await manager.signinSilentCallback();
            } catch {
              // Swallow errors in the iframe — the parent window's
              // signinSilent() promise handles failure via its own timeout.
            }
            return;
          }

          if (window.opener) {
            try {
              await manager.signinPopupCallback();
            } catch {
              window.close();
            }
            return;
          }

          try {
            const callbackUser = await manager.signinRedirectCallback();
            await handleUserLoaded(
              callbackUser,
              isGoogleCallback ? "google" : "azure"
            );
            const returnPath =
              typeof callbackUser.state === "string" && callbackUser.state
                ? callbackUser.state
                : "/";
            navigate(returnPath, { replace: true });
            return;
          } catch {
            // State missing from storage — stale callback URL or silent
            // renew broke out of its iframe. Navigate away and fall through
            // to the getUser() path below.
            navigate("/", { replace: true });
          }
        }

        const googleUser = await googleManager.getUser();
        if (googleUser && !googleUser.expired) {
          await handleUserLoaded(googleUser, "google");
          return;
        }

        const azureUser = await azureManager.getUser();
        if (azureUser && !azureUser.expired) {
          await handleUserLoaded(azureUser, "azure");
          return;
        }
      } catch (err) {
        reportError(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    }

    void checkExistingSession();

    const loginChannel = createBroadcastChannel(loginChannelName);
    loginChannel.onmessage = (msg) => {
      const payload = msg.data || msg;
      if (payload.tag !== "AuthorizationsUpdated") {
        return;
      }

      // A session arriving from another tab must be held to the same expiry
      // terms as one established locally. The previous `{ id_token } as User`
      // cast produced an object with no expires_at, so `User.expired` was
      // undefined and every expiry check against it passed vacuously.
      if (!payload.token || isTokenExpired(payload.token)) {
        setLoading(false);
        return;
      }

      // Safe to read directly: the isTokenExpired guard above has already
      // rejected any token whose exp is absent, unreadable, or non-finite.
      const expiresAt = Math.floor(
        getExpiration(payload.token).getTime() / 1000
      );

      setUser({
        id_token: payload.token,
        expires_at: expiresAt,
        get expired() {
          return expiresAt * 1000 <= Date.now();
        },
      } as User);
      setProvider(payload.provider ?? null);
      setAuthorizations(payload.authorizations);
      setLoading(false);
    };

    const logoutChannel = createBroadcastChannel(logoutChannelName);
    logoutChannel.onmessage = () => {
      setUser(null);
      setProvider(null);
      setAuthorizations(null);
    };

    return () => {
      googleManager.events.removeUserLoaded(onGoogleLoaded);
      googleManager.events.removeUserUnloaded(handleUserUnloaded);
      azureManager.events.removeUserLoaded(onAzureLoaded);
      azureManager.events.removeUserUnloaded(handleUserUnloaded);
      loginChannel.close();
      logoutChannel.close();
    };
  }, [handleUserLoaded, handleUserUnloaded, location.pathname, navigate]);

  const dispatch = useCallback(
    (action: any) => {
      if (action.tag === "Logout") {
        void doLogout(action.preventPublish);
      }
    },
    [doLogout]
  );

  const isAuthenticated = user !== null && authorizations !== null;

  return (
    <UserActiveProvider>
      <AuthenticationManagerDisplay
        isAuthenticated={isAuthenticated}
        loading={loading}
        error={error}
        jwt={user?.id_token ?? null}
        authorizations={authorizations}
        provider={provider}
        dispatch={dispatch}
      >
        {children}
      </AuthenticationManagerDisplay>
    </UserActiveProvider>
  );
};

function useAuthenticationManagerContext(
  caller: string
): AuthenticationManagerContextData {
  const ctx = useContext(AuthenticationManagerContext);

  if (ctx === null) {
    throw new Error(`Do not call ${caller} outside of AuthenticationManager`);
  }

  return ctx;
}

export function useRawJwt(): string | null {
  const ctx = useAuthenticationManagerContext("useRawJwt");

  return ctx?.jwt;
}

export function useAuthorizations(): Authorizations | null {
  const ctx = useAuthenticationManagerContext("useAuthorizations");

  return ctx?.authorizations;
}

/**
 * The provider that issued the current session. Renewal must target this
 * manager — inferring it from the email domain guaranteed a silent-renew failure
 * for any Google user outside the canyon-trail.com domain.
 */
export function useSessionProvider(): OidcProvider | null {
  const ctx = useAuthenticationManagerContext("useSessionProvider");

  return ctx?.provider ?? null;
}

export function useLogout(): (preventPublishToOtherWindows?: boolean) => void {
  const ctx = useAuthenticationManagerContext("useLogout");

  return (preventPublishToOtherWindows?: boolean) => {
    if (preventPublishToOtherWindows) {
      ctx.dispatch({ tag: "Logout", preventPublish: true });
    } else {
      ctx.dispatch({ tag: "Logout" });
    }
  };
}
